import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import * as React from "react";
import { FormAddress } from "src/schemas/Address";
import { toFormAddress } from "src/services/googlePlaces/transforms";

const googleApiLibraries: Array<"places"> = ["places"];
const googleAutocompleteResultFields: Array<string> = [
  "address_components",
  "geometry",
];

// https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-multiple-countries
const USA_AND_UNINCORPORATED_ORGANIZED_TERRITORIES = [
  "us",
  "pr",
  "vi",
  "gu",
  "mp",
];
interface Props {
  children: React.ReactElement;
  onAddressSelected?: (address: FormAddress) => void;
}

export const AddressAutocomplete = ({
  children,
  onAddressSelected,
}: Props): React.ReactElement => {
  const ref = React.useRef<Autocomplete>(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY || "",
    libraries: googleApiLibraries,
  });
  const [autocomplete, setAutocomplete] =
    React.useState<google.maps.places.Autocomplete>();

  const handleLoad = (ac: google.maps.places.Autocomplete) => {
    /**
     * Currently, when user select the address using enter key, it will also trigger form submission, which is not desireable.
     * Pressing enter when selecting address from dropdown should only trigger address selection, not form submission.
     * I can't figure out how to disable the form submission behavior when the address selection is visible.
     * So for now, we just always disable the Enter button for form submission. - Abadi Kurniawan
     */
    if (ref.current) {
      ref.current.containerElement.current?.addEventListener(
        "keydown",
        preventEnter
      );
      // Google appends the suggestions dropdown (.pac-container) to document.body
      // at a z-index below Chakra's modal, so it is occluded when this input is
      // rendered inside a Modal. The dropdown is created lazily on first focus, so
      // lift it above the modal whenever the input is interacted with.
      ref.current.containerElement.current?.addEventListener(
        "focusin",
        liftPacContainers
      );
      ref.current.containerElement.current?.addEventListener(
        "keyup",
        liftPacContainers
      );
    }

    ac.setComponentRestrictions({
      country: USA_AND_UNINCORPORATED_ORGANIZED_TERRITORIES,
    });

    setAutocomplete(ac);
  };

  const triggerAddressSelected = () => {
    if (!onAddressSelected) return;

    const place = autocomplete?.getPlace();
    if (!place?.address_components) return;

    const address = toFormAddress(
      place.address_components,
      place.geometry?.location
    );
    onAddressSelected(address);
  };

  // Fall back to plain input if the Google API fails to load.
  if (!isLoaded || loadError) return children;

  return (
    <Autocomplete
      onLoad={handleLoad}
      onPlaceChanged={triggerAddressSelected}
      fields={googleAutocompleteResultFields}
      ref={ref}
    >
      {children}
    </Autocomplete>
  );
};

function preventEnter(key: KeyboardEvent) {
  if (key.code === "Enter") {
    key.preventDefault();
  }
}

// Raise Google's body-mounted suggestions dropdown above the Chakra modal
// (zIndices.modal = 1400) so it is not occluded when the input is inside a Modal.
function liftPacContainers() {
  document
    .querySelectorAll<HTMLElement>(".pac-container")
    .forEach((element) => {
      element.style.zIndex = "var(--chakra-zIndices-tooltip)";
    });
}
